import React, { useMemo } from 'react'
import ReactSelect, { components } from 'react-select'
import type { Props, StylesConfig } from 'react-select'

export interface Option {
  label: string
  value: string
  email?: string
  name?: string
  companyName?: string
  userType?: string
}

interface MultiSelectProps {
  options: Option[]
  value?: Option | Option[] | null
  onChange: (selected: Option | Option[] | null) => void
  placeholder?: string
  showEmailInOption?: boolean
  isMulti?: boolean // New prop to control if multiple selection is allowed
  isDisabled?: boolean
  isLoading?: boolean // Show loading spinner in the select
  customControlStyles?: React.CSSProperties
}

// Function to create styles with custom control styles
const createSelectStyles = (
  customControlStyles?: React.CSSProperties,
): StylesConfig<Option, boolean> => ({
  control: (baseStyles) => ({
    ...baseStyles,
    borderColor: 'rgb(209, 213, 219)',
    fontSize: '14px',
    '&:hover': {
      borderColor: 'rgb(209, 213, 219)',
    },
    ...customControlStyles,
  }),
  option: (baseStyles, state) => ({
    ...baseStyles,
    fontSize: '14px',
    backgroundColor: state.isFocused ? '#F3F4F6' : 'white',
    color: state.isFocused ? '#1C64F2' : '#374151',
    cursor: 'pointer',
    '&:active': {
      backgroundColor: '#F3F4F6',
    },
  }),
  placeholder: (baseStyles) => ({
    ...baseStyles,
    fontSize: '14px',
    color: '#9CA3AF',
  }),
  menuPortal: (baseStyles) => ({
    ...baseStyles,
    zIndex: 9999,
  }),
  menu: (baseStyles) => ({
    ...baseStyles,
    backgroundColor: 'white',
    border: '1px solid rgb(209, 213, 219)',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    marginTop: '4px',
  }),
})

// Static styles to avoid recreation
const optionContainerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
}

const emailStyle = {
  fontSize: '12px',
  color: '#6B7280',
  marginLeft: '8px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}

// Define custom components outside the main component
// biome-ignore lint/suspicious/noExplicitAny: react-select component props are complex
const CustomOption = React.memo(function CustomOption(props: any) {
  const option = props.data

  return (
    <components.Option {...props}>
      <div style={optionContainerStyle}>
        <div style={{ fontWeight: props.isFocused ? 500 : 400 }}>
          {option.name || option.label}
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
          }}
        >
          {option.email && props.selectProps.showEmailInOption && (
            <div style={emailStyle}>{option.email}</div>
          )}
          {option.companyName && (
            <div style={{ ...emailStyle, color: '#6B7280', fontSize: '11px' }}>
              {option.companyName}
            </div>
          )}
          {option.userType && (
            <div
              style={{
                ...emailStyle,
                color: '#9CA3AF',
                fontSize: '10px',
                textTransform: 'uppercase',
              }}
            >
              {option.userType}
            </div>
          )}
        </div>
      </div>
    </components.Option>
  )
})

const CustomMultiValueLabel = React.memo(function CustomMultiValueLabel(
  // biome-ignore lint/suspicious/noExplicitAny: react-select MultiValueLabel props
  props: any,
) {
  const option = props.data
  return (
    <components.MultiValueLabel {...props}>
      {option.name ||
        (option.email ? option.label.split(' (')[0] : option.label)}
    </components.MultiValueLabel>
  )
})

const MultiSelect: React.FC<MultiSelectProps> = ({
  options,
  value,
  onChange,
  placeholder,
  showEmailInOption = false,
  isMulti = true,
  isDisabled = false,
  isLoading = false,
  customControlStyles,
}) => {
  // Memoize the options to prevent recreating on each render
  const memoizedOptions = useMemo(() => {
    return options.map((option) => ({
      ...option,
      // Use both id and value to ensure uniqueness
      key: `${option.value}-${option.email || ''}`,
    }))
  }, [options])

  // Memoize the components object to prevent recreation
  const customComponents = useMemo(
    () => ({
      Option: CustomOption,
      MultiValueLabel: CustomMultiValueLabel,
    }),
    [],
  )

  // Pass showEmailInOption through a custom attribute
  // biome-ignore lint/suspicious/noExplicitAny: react-select props are complex
  const props: any = {
    isMulti,
    isDisabled,
    isLoading,
    instanceId: useMemo(() => Math.random().toString(36).substr(2, 9), []),
    options: memoizedOptions,
    value,
    onChange: (selected: Option | Option[] | null) => {
      if (isMulti) {
        onChange(selected as Option[])
      } else {
        // Handle null case for single select
        onChange(selected ? (selected as Option) : null)
      }
    },
    styles: createSelectStyles(customControlStyles),
    placeholder,
    menuPortalTarget: document.body,
    menuPosition: 'fixed' as const, // Use fixed instead of absolute for more stable positioning
    menuPlacement: 'auto' as const,
    components: customComponents,
    isSearchable: true,
    classNamePrefix: 'react-select',
    backspaceRemovesValue: true,
    menuShouldScrollIntoView: false,
    menuShouldBlockScroll: true, // Prevent page scrolling when menu is open
    captureMenuScroll: true,
  }

  // Add our custom props that will be accessible in the custom components
  props.showEmailInOption = showEmailInOption

  return <ReactSelect {...props} />
}

export default MultiSelect
